'use client'

import { CoverImageProjectFragment } from '@/gql/generated/graphql'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { usePrivy } from '@privy-io/react-auth'
import * as Dialog from '@radix-ui/react-dialog'
import { useSelector } from '@xstate/store/react'
import { ArtBlocksWordmarkLockupFullColor } from 'design-configuration/components'
import { AnimatePresence, motion } from 'motion/react'
import { useSession } from 'next-auth/react'
import { useEffect, useMemo } from 'react'
import { useClient } from 'urql'
import { Hex } from 'viem'
import { usePublicClient } from 'wagmi'
import { ChoosePathStep } from './choose-path-step'
import { ConfirmationStep } from './confirmation-step'
import { LinkAccountsStep } from './link-accounts-step'
import { ProfileSetupStep } from './profile-setup-step'
import { onboardingStore } from './store'
import { WelcomeStep } from './welcome-step'
import { AddressDisplay } from '@components/address-display'

export function Onboarding({
  coverImageProjects
}: {
  coverImageProjects: CoverImageProjectFragment[]
}): React.ReactNode {
  const { user, isModalOpen } = usePrivy()

  const { data: session, update: updateSession } = useSession()
  const publicClient = usePublicClient()
  const urqlClient = useClient()
  useEffect(() => {
    onboardingStore.trigger.externalContextUpdated({ session, updateSession, publicClient, urqlClient })
  }, [session, updateSession, publicClient, urqlClient])

  const activeSteps = useSelector(onboardingStore, ({ context }) => {
    return context.activeSteps
  })

  const open = useSelector(onboardingStore, ({ context }) => {
    return context.open
  })

  const step = useSelector(onboardingStore, ({ context }) => {
    return context.step
  })

  const userAccounts = useMemo(() => {
    return user?.linkedAccounts.filter((account) => account.type === 'wallet').map((account) => account.address) ?? []
  }, [user])
  useEffect(() => {
    onboardingStore.trigger.userAccountsUpdated({ userAccounts: userAccounts as Hex[] })
  }, [userAccounts])

  const renderStep = () => {
    switch (step) {
      case 'WELCOME':
        return <WelcomeStep />
      case 'CHOOSE_PATH':
        return <ChoosePathStep />
      case 'LINK_ACCOUNTS':
        return <LinkAccountsStep coverImageProjects={coverImageProjects} />
      case 'PROFILE_SETUP':
        return <ProfileSetupStep />
      case 'CONFIRMATION':
        return <ConfirmationStep coverImageProjects={coverImageProjects} />
    }
  }

  const currentCoverImageProject = (coverImageProjects[activeSteps.indexOf(step)] ?? coverImageProjects[0]) as
    | CoverImageProjectFragment
    | undefined

  return (
    <Dialog.Root open={open}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay className="fixed inset-0" />
            <Dialog.Content
              className="bg-surface fixed inset-0 z-50 overflow-auto lg:overflow-hidden"
              trapFocus={!isModalOpen}
              asChild
            >
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Dialog.Title className="sr-only">Onboarding</Dialog.Title>
                <div className="flex min-h-full flex-col-reverse lg:h-full lg:flex-row">
                  <div className="flex flex-1 lg:min-w-[700px]">
                    <AnimatePresence mode="wait">
                      {step === 'WELCOME' ? (
                        <motion.div
                          className="flex-1"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          key={'welcome-step'}
                        >
                          {renderStep()}
                        </motion.div>
                      ) : (
                        <motion.div
                          className="flex flex-1 flex-col lg:h-full lg:overflow-auto"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          key={'onboarding-step-container'}
                        >
                          <div className="p-md flex items-center justify-between">
                            <ArtBlocksWordmarkLockupFullColor className="h-lg fill-on-surface" />
                            <div className="flex items-center gap-0.5">
                              {new Array(activeSteps.length - 1).fill(null).map((_, index) => {
                                return (
                                  <div
                                    key={index}
                                    className="h-2xs w-md bg-primary/20 relative overflow-hidden rounded-[1px]"
                                  >
                                    <motion.div
                                      className="bg-primary absolute inset-0"
                                      initial={{ y: '100%' }}
                                      animate={{ y: step === activeSteps[index + 1] ? '0%' : '100%' }}
                                      transition={{ ease: 'easeOut', duration: 0.4 }}
                                    />
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                          <AnimatePresence mode="wait">
                            <motion.div
                              className="relative flex-1"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              key={step}
                            >
                              {renderStep()}
                            </motion.div>
                          </AnimatePresence>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="h-md relative lg:h-auto lg:flex-1">
                    <AnimatePresence>
                      <motion.div
                        className="absolute inset-0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        key={currentCoverImageProject?.featured_token?.[0]?.media_url ?? currentCoverImageProject?.name}
                      >
                        <ImageWithCustomLoader
                          src={currentCoverImageProject?.featured_token?.[0].media_url ?? ''}
                          alt={currentCoverImageProject?.name ?? ''}
                          fill
                          className="object-cover"
                          sizes="50vw"
                        />
                        <div className="absolute inset-0 z-10 shadow-[3px_4px_10px_0px_rgba(0,0,0,0.25)_inset]" />
                        <div className="py-2xs px-xs text-detail text-on-surface-inverted bg-surface-inverted/80 absolute bottom-0 right-0 z-20 hidden backdrop-blur-sm lg:block">
                          <span className="italic">
                            {currentCoverImageProject?.name} #
                            {currentCoverImageProject?.featured_token?.[0]?.invocation}{' '}
                          </span>
                          <span>
                            by {currentCoverImageProject?.artist_name}, owned by{' '}
                            <AddressDisplay
                              address={currentCoverImageProject?.featured_token?.[0]?.owner_address as `0x${string}`}
                            />
                          </span>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  )
}
